/**
 * The voice-note player's pure logic: heard ranges, skips and stamps. No DOM, no React —
 * the player and the bubble read these; the tests read all of it.
 */

/** The skip buttons' default step (the stored preference `voiceSkipSec` overrides it). */
export const SKIP_SECONDS = 5;

/** Playback speeds, in the order the pill cycles them: 1× → 1.5× → 2× → 0.5× → 1×. */
export const RATES = [1, 1.5, 2, 0.5];
export function nextRate(rate) {
  const i = RATES.indexOf(rate);
  return RATES[(i + 1) % RATES.length] || 1;
}
export function rateLabel(rate) { return `${rate}×`; }

/** Hold a skip button this long and the fan of steps opens. */
export const SKIP_HOLD_MS = 380;

/** The notes list: this tall in the dock, this tall while the keyboard is up. */
export const NOTES_MAX_PX = 196;
export const NOTES_MAX_KEYBOARD_PX = 108;

/** How often the heard ranges are saved while a note is playing (ms). */
export const LISTEN_SAVE_MS = 4000;

/** A stretch counted as heard only once it is this long: a seek lands, it does not listen. */
export const MIN_RANGE_SEC = 0.25;

const round1 = (n) => Math.round(n * 10) / 10;

/**
 * Sorted, merged, non-overlapping [from, to] pairs, clamped to `duration` when known.
 * Mirrors `VoiceListenRanges.Normalize` on the API so the client never shows a shape
 * the server would store differently.
 */
export function mergeRanges(ranges, duration = 0) {
  const max = duration > 0 ? duration : Infinity;
  const clean = [];
  (ranges || []).forEach((r) => {
    if (!Array.isArray(r) || r.length < 2) return;
    let [a, b] = r;
    if (!Number.isFinite(a) || !Number.isFinite(b)) return;
    if (b < a) [a, b] = [b, a];
    a = round1(Math.min(Math.max(a, 0), max));
    b = round1(Math.min(Math.max(b, 0), max));
    if (b - a < 0.05) return;
    clean.push([a, b]);
  });
  clean.sort((x, y) => x[0] - y[0]);
  const out = [];
  clean.forEach((r) => {
    const last = out[out.length - 1];
    if (last && r[0] <= last[1] + 0.1) last[1] = Math.max(last[1], r[1]);
    else out.push([r[0], r[1]]);
  });
  return out;
}

/** `ranges` with one more heard stretch folded in. */
export function addRange(ranges, from, to, duration = 0) {
  if (!Number.isFinite(from) || !Number.isFinite(to) || to - from < MIN_RANGE_SEC) return mergeRanges(ranges, duration);
  return mergeRanges([...(ranges || []), [from, to]], duration);
}

/** Seconds heard, as a fraction of the note (0 when the length is unknown). */
export function listenedFraction(ranges, duration) {
  if (!(duration > 0)) return 0;
  const covered = mergeRanges(ranges, duration).reduce((s, [a, b]) => s + (b - a), 0);
  return Math.min(1, covered / duration);
}

/** True once (nearly) the whole note has been heard. */
export function fullyHeard(ranges, duration) {
  return duration > 0 && listenedFraction(ranges, duration) >= 0.97;
}

/** Where a skip lands: never before the start, never past the end. */
export function clampSeek(t, duration) {
  const max = duration > 0 ? duration : Infinity;
  return Math.min(Math.max(t || 0, 0), max);
}

/** The ranges as CSS: a left/width pair in % for each heard stretch. */
export function rangeSegments(ranges, duration) {
  if (!(duration > 0)) return [];
  return mergeRanges(ranges, duration).map(([a, b]) => ({ left: (a / duration) * 100, width: ((b - a) / duration) * 100 }));
}

/** "0:07" — a stamp on a note or on the clock, floored so it never reads ahead of the audio. */
export function formatStamp(sec) {
  const s = Math.max(0, Math.floor(Number.isFinite(sec) ? sec : 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** The stamp a note takes: the clock when the FIRST character was typed, kept for the draft. */
export function stampForDraft({ draft, next, stamp, now }) {
  if (!next.trim()) return null;
  if (!draft.trim() && stamp == null) return round1(now);
  return stamp;
}

// ── Search + filter (Part C §4b) ──

/** Does this note match the query: its words (case-insensitive), its stamp (`2:` finds 2:00–2:59),
 *  or `bookmark` for an empty one. An empty query matches everything. */
export function noteMatchesQuery(note, q) {
  const needle = (q || '').trim().toLowerCase();
  if (!needle) return true;
  const text = (note.body || 'bookmark').toLowerCase();
  if (text.includes(needle)) return true;
  return formatStamp(note.atSec).startsWith(needle);
}

/** `all` · `mine` · `theirs` · `bookmarks` */
export function noteMatchesFilter(note, filter) {
  if (filter === 'mine') return note.mine !== false;
  if (filter === 'theirs') return note.mine === false;
  if (filter === 'bookmarks') return !note.body;
  return true;
}

/** The notes that pass both, in time order. One helper for the dock and (later) the transcript. */
export function matchNotes(notes, q, filter = 'all') {
  return (notes || []).filter((n) => noteMatchesFilter(n, filter) && noteMatchesQuery(n, q))
    .slice().sort((a, b) => a.atSec - b.atSec || new Date(a.createdAt) - new Date(b.createdAt));
}

/** True when the stamp pill itself should light up for this query. */
export function stampMatches(atSec, q) {
  const needle = (q || '').trim().toLowerCase();
  return !!needle && formatStamp(atSec).startsWith(needle);
}

/** `body` cut into `{ text, hit }` runs so the matched text can be drawn bold on pink. */
export function highlightParts(body, q) {
  const text = body || '';
  const needle = (q || '').trim().toLowerCase();
  if (!needle) return [{ text, hit: false }];
  const lo = text.toLowerCase();
  const out = [];
  let i = 0;
  let j = lo.indexOf(needle, i);
  while (j !== -1) {
    if (j > i) out.push({ text: text.slice(i, j), hit: false });
    out.push({ text: text.slice(j, j + needle.length), hit: true });
    i = j + needle.length;
    j = lo.indexOf(needle, i);
  }
  if (i < text.length) out.push({ text: text.slice(i), hit: false });
  return out.length ? out : [{ text, hit: false }];
}

/** The line above the results: `3 OF 7 NOTES · RETURN JUMPS TO 0:42` / `… IN THIS CHAT`. */
export function searchCountLine({ shown, total, wholeChat, hasQuery, filter }) {
  const scope = wholeChat ? ' IN THIS CHAT' : '';
  let line = `${shown.length} OF ${total} NOTES${scope}`;
  if (shown.length && (hasQuery || filter !== 'all')) line += ` · RETURN JUMPS TO ${formatStamp(shown[0].atSec)}`;
  return line;
}


/** How often the transcript card asks again while the note is still being transcribed (ms). */
export const TRANSCRIPT_POLL_MS = 2500;
/** Give up polling after this long; the card then offers RETRY. */
export const TRANSCRIPT_POLL_MAX_MS = 3 * 60 * 1000;

/** The index of the paragraph the clock is in, or the last one that has started; -1 before any. */
export function currentParagraph(paragraphs, at) {
  if (!Array.isArray(paragraphs) || !paragraphs.length || !Number.isFinite(at)) return -1;
  let idx = -1;
  for (let i = 0; i < paragraphs.length; i += 1) {
    if (paragraphs[i].startSec <= at + 0.05) idx = i; else break;
  }
  return idx;
}
